import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Trash2, Loader2, Save, Plus, X, AlertTriangle,
} from "lucide-react";
import Star from "lucide-react/dist/esm/icons/star";
import { toast } from "sonner";
import { api } from "../lib/api";
import { gbp, ukDate } from "../lib/format";
import {
  PageHeader, Card, Field, Input, NumberInput, Textarea, Select,
  PrimaryButton, QuietButton,
} from "../components/UI";
import { PriceHistoryPanel } from "../components/PriceHistoryPanel";

const cls = (...a) => a.filter(Boolean).join(" ");

/* LT-18 §2 — local near-match normaliser for CATEGORIES (no endpoint
 * needed). LT-18c §3: the corporate-suffix strip is suppliers-only and lives
 * in the backend's one shared normaliser; categories never get it. */
function normForMatch(s) {
  if (!s) return "";
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}
function scoreMatch(q, c) {
  if (!q || !c) return 0;
  if (q === c) return 1;
  if (c.startsWith(q) || q.startsWith(c)) {
    const ratio = Math.min(q.length, c.length) / Math.max(q.length, c.length);
    return 0.85 * ratio + 0.15;
  }
  if (c.includes(q) || q.includes(c)) {
    const ratio = Math.min(q.length, c.length) / Math.max(q.length, c.length);
    return 0.60 * ratio;
  }
  return 0;
}

/* Confirm-on-new dialog — shared by category and supplier pickers. */
function ConfirmOnNewDialog({ open, entity, typed, candidates,
                             onSelectExisting, onCreateNew, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid={`confirm-on-new-${entity}`}>
      <div className="bg-surface border border-line rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-5 py-4 border-b border-line flex items-center gap-2">
          <AlertTriangle size={16} className="text-note-amber" />
          <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
            Add "{typed}" as a new {entity}?
          </h3>
        </div>
        <div className="px-5 py-4">
          {candidates.length > 0 && (
            <>
              <p className="text-ink-3 mb-2" style={{ fontSize: 12 }}>
                You may have meant one of these:
              </p>
              <div className="space-y-1 mb-4">
                {candidates.map((c) => (
                  <button
                    key={c.id || c.key}
                    type="button"
                    onClick={() => onSelectExisting(c)}
                    className="w-full text-left px-3 py-2 rounded border border-line hover:bg-surface-2/50"
                    data-testid={`confirm-on-new-candidate-${c.id || c.key}`}
                  >
                    <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>
                      {c.name || c.label}
                    </span>
                    {c.score !== undefined && (
                      <span className="ml-2 text-ink-4 font-mono" style={{ fontSize: 10.5 }}>
                        score {(c.score * 100).toFixed(0)}%
                      </span>
                    )}
                    <span className="ml-2 text-ink-3" style={{ fontSize: 11 }}>Use this</span>
                  </button>
                ))}
              </div>
            </>
          )}
          <p className="text-ink-3 mb-3" style={{ fontSize: 12 }}>
            If none of these are it, add "{typed}" as a new {entity}.
          </p>
          <div className="flex items-center justify-end gap-2">
            <QuietButton onClick={onCancel} data-testid={`confirm-on-new-cancel-${entity}`}>Cancel</QuietButton>
            <PrimaryButton icon={Plus} onClick={onCreateNew}
              data-testid={`confirm-on-new-create-${entity}`}>
              Add "{typed}"
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Supplier row inside the form's supplier_links[] section. */
function SupplierLinkRow({ link, suppliers, onChange, onRemove, canRemove,
                          onNewSupplierNeeded }) {
  const supplier = suppliers.find((s) => s.id === link.supplier_id);
  const [typing, setTyping] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const active = supplier;
  const filteredSuppliers = useMemo(() => {
    const q = typing.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter((s) => (s.name || "").toLowerCase().includes(q));
  }, [suppliers, typing]);
  const trySelect = () => {
    const q = typing.trim();
    if (!q) return;
    const exact = suppliers.find((s) => (s.name || "").toLowerCase() === q.toLowerCase());
    if (exact) {
      onChange({ ...link, supplier_id: exact.id });
      setTyping("");
      setShowPicker(false);
      return;
    }
    // No match → fire the confirm-on-new dialog. Parent looks up candidates
    // against the backend near-match endpoint.
    onNewSupplierNeeded(q, (selectedOrNew) => {
      if (selectedOrNew) {
        onChange({ ...link, supplier_id: selectedOrNew.id });
      }
      setTyping("");
      setShowPicker(false);
    });
  };

  return (
    <div className="grid gap-3 pb-4 border-b border-line-soft last:border-b-0"
      style={{ gridTemplateColumns: "2fr 1fr 1fr 90px 40px" }}
      data-testid={`supplier-link-row-${link.__key || "new"}`}>
      <div>
        <label className="text-ink-3 uppercase" style={{ fontSize: 10, letterSpacing: "0.05em" }}>Supplier</label>
        {active && !showPicker ? (
          <button type="button"
            onClick={() => { setShowPicker(true); setTyping(active.name || ""); }}
            className="w-full h-9 px-2.5 rounded-md border border-line bg-surface text-ink text-left"
            style={{ fontSize: 13 }}
            data-testid={`supplier-picker-current-${link.__key}`}>
            {active.name}
            {active.status === "archived" && (
              <span className="ml-2 text-ink-3" style={{ fontSize: 11 }}>(archived)</span>
            )}
          </button>
        ) : (
          <div className="relative">
            <Input value={typing} onChange={setTyping}
              placeholder="Start typing a supplier…"
              onBlur={() => setTimeout(() => setShowPicker(false), 180)}
              data-testid={`supplier-picker-input-${link.__key}`} />
            {(typing.trim() || showPicker) && (
              <div className="absolute left-0 right-0 top-full mt-0.5 z-20 bg-surface border border-line rounded-md shadow-card max-h-56 overflow-auto">
                {filteredSuppliers.length === 0 ? (
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); trySelect(); }}
                    className="w-full text-left px-3 py-2 hover:bg-surface-2/60"
                    style={{ fontSize: 12 }}
                    data-testid={`supplier-picker-none-${link.__key}`}>
                    <span className="text-note-amber">
                      No supplier matches "{typing.trim()}" — press Enter to add
                    </span>
                  </button>
                ) : (
                  filteredSuppliers.slice(0, 10).map((s) => (
                    <button key={s.id} type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onChange({ ...link, supplier_id: s.id });
                        setTyping("");
                        setShowPicker(false);
                      }}
                      className="w-full text-left px-3 py-1.5 hover:bg-surface-2/60"
                      style={{ fontSize: 12 }}
                      data-testid={`supplier-picker-option-${s.id}`}>
                      {s.name}
                    </button>
                  ))
                )}
                {filteredSuppliers.length > 0 && typing.trim() && (
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); trySelect(); }}
                    className="w-full text-left px-3 py-1.5 border-t border-line-soft hover:bg-surface-2/60"
                    style={{ fontSize: 11.5 }}
                    data-testid={`supplier-picker-add-${link.__key}`}>
                    <Plus size={11} className="inline mr-1" />
                    Add "{typing.trim()}" as new supplier
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div>
        <label className="text-ink-3 uppercase" style={{ fontSize: 10, letterSpacing: "0.05em" }}>Their code</label>
        <Input value={link.supplier_ref || ""}
          onChange={(v) => onChange({ ...link, supplier_ref: v })}
          placeholder="e.g. SKU-12345"
          mono
          data-testid={`supplier-link-ref-${link.__key}`} />
      </div>
      <div>
        <label className="text-ink-3 uppercase" style={{ fontSize: 10, letterSpacing: "0.05em" }}>Their price</label>
        <NumberInput value={link.supplier_price}
          onChange={(v) => onChange({ ...link, supplier_price: v })}
          prefix="£"
          data-testid={`supplier-link-price-${link.__key}`} />
      </div>
      <div className="flex items-end">
        <label className="inline-flex items-center gap-1.5 h-9"
          style={{ fontSize: 12 }}
          data-testid={`supplier-link-preferred-label-${link.__key}`}>
          <input type="radio" name="preferred_supplier" checked={!!link.is_preferred}
            onChange={() => onChange({ ...link, is_preferred: true })}
            data-testid={`supplier-link-preferred-${link.__key}`} />
          <Star size={12} className={cls("shrink-0", link.is_preferred ? "text-accent" : "text-ink-4")} />
          <span className="text-ink-3">Preferred</span>
        </label>
      </div>
      <div className="flex items-end justify-end">
        {canRemove && (
          <button type="button" onClick={onRemove}
            className="h-9 w-9 flex items-center justify-center text-ink-3 hover:text-note-red"
            title="Remove this supplier"
            data-testid={`supplier-link-remove-${link.__key}`}>
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}


export default function MaterialEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = !!id;

  const [form, setForm] = useState({
    code: "", name: "", unit: "", current_price: 0, category: "",
    coverage_per_unit: null, coverage_unit: "", default_wastage_pct: null,
    stock_length: null, keywords: "",
    notes: "",
    supplier_links: [],
  });
  // LT-18c §5 — coverage_unit == unit is a WARNING with an override at the
  // form (a person is here to judge); the importer rejects it outright.
  const [coverageOverride, setCoverageOverride] = useState(false);
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editing);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  // Confirm-on-new dialog state — one dialog serving both category + supplier flows.
  const [confirmDlg, setConfirmDlg] = useState(null); // { entity, typed, candidates, onSelect, onCreate }
  const [newCategoryTyped, setNewCategoryTyped] = useState("");

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  /* --- taxonomies --- */
  const loadTaxonomies = useCallback(async () => {
    const [cats, us, sups] = await Promise.all([
      api.get("/material-categories"),
      api.get("/material-units"),
      // suppliers may include archived so a material's existing archived
      // link still resolves to a name; the picker filters live only.
      api.get("/suppliers", { params: { include_archived: true } }),
    ]);
    setCategories(cats.data || []);
    setUnits(us.data || []);
    setSuppliers(sups.data || []);
  }, []);
  useEffect(() => { loadTaxonomies(); }, [loadTaxonomies]);

  /* --- load material --- */
  useEffect(() => {
    if (!editing) { setLoading(false); return; }
    api.get(`/materials/${id}`).then((r) => {
      setDoc(r.data);
      setForm({
        code: r.data.code, name: r.data.name, unit: r.data.unit,
        current_price: r.data.current_price,
        category: r.data.category || "",
        coverage_per_unit: r.data.coverage_per_unit ?? null,
        coverage_unit: r.data.coverage_unit || "",
        default_wastage_pct: r.data.default_wastage_pct ?? null,
        stock_length: r.data.stock_length ?? null,
        keywords: (r.data.keywords || []).join("; "),
        notes: r.data.notes || "",
        supplier_links: (r.data.supplier_links || []).map((link, i) => ({
          ...link, __key: `existing-${i}`,
        })),
      });
    }).catch(() => toast.error("Material not found"))
      .finally(() => setLoading(false));
  }, [id, editing]);

  /* --- derived: cost_per_coverage --- */
  const costPerCoverage = useMemo(() => {
    const price = Number(form.current_price || 0);
    const cov = Number(form.coverage_per_unit || 0);
    if (!(price > 0) || !(cov > 0)) return null;
    return price / cov;
  }, [form.current_price, form.coverage_per_unit]);

  /* --- supplier_links helpers --- */
  const upsertLink = (idx, patch) => {
    setForm((f) => {
      const links = [...(f.supplier_links || [])];
      // if patch sets is_preferred=true, clear all others (invariant)
      const clearOthers = patch.is_preferred === true;
      links[idx] = { ...links[idx], ...patch };
      if (clearOthers) {
        for (let i = 0; i < links.length; i++) {
          if (i !== idx) links[i] = { ...links[i], is_preferred: false };
        }
      }
      return { ...f, supplier_links: links };
    });
  };
  const addLink = () => {
    setForm((f) => {
      const links = [...(f.supplier_links || [])];
      const hasPref = links.some((l) => l.is_preferred);
      links.push({
        supplier_id: "",
        supplier_ref: null,
        supplier_price: null,
        is_preferred: !hasPref,
        __key: `new-${Date.now()}`,
      });
      return { ...f, supplier_links: links };
    });
  };
  const removeLink = (idx) => {
    setForm((f) => {
      const links = [...(f.supplier_links || [])];
      const wasPreferred = links[idx]?.is_preferred;
      links.splice(idx, 1);
      // promote first remaining if we removed the preferred one
      if (wasPreferred && links.length > 0) {
        links[0] = { ...links[0], is_preferred: true };
      }
      return { ...f, supplier_links: links };
    });
  };

  /* --- confirm-on-new: supplier --- */
  const openSupplierDialog = async (typed, onDialogFinish) => {
    try {
      const r = await api.post("/suppliers/near-match", { name: typed });
      const candidates = (r.data?.candidates || []).map((c) => ({ ...c.supplier, score: c.score }));
      setConfirmDlg({
        entity: "supplier",
        typed,
        candidates,
        onSelect: (selected) => {
          setConfirmDlg(null);
          onDialogFinish(selected);
        },
        onCreate: async () => {
          try {
            const c = await api.post("/suppliers", { name: typed });
            setSuppliers((s) => [...s, c.data]);
            setConfirmDlg(null);
            onDialogFinish(c.data);
            toast.success(`Added supplier "${typed}"`);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to add supplier");
          }
        },
        onCancel: () => {
          setConfirmDlg(null);
          onDialogFinish(null);
        },
      });
    } catch {
      toast.error("Failed to fetch supplier matches");
      onDialogFinish(null);
    }
  };

  /* --- confirm-on-new: category (frontend near-match) --- */
  const openCategoryDialog = (typed) => {
    const qNorm = normForMatch(typed);
    const candidates = categories
      .filter((c) => c.status !== "archived")
      .map((c) => ({ ...c, score: scoreMatch(qNorm, normForMatch(c.label)) }))
      .filter((c) => c.score >= 0.5)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
    setConfirmDlg({
      entity: "category",
      typed,
      candidates,
      onSelect: (selected) => {
        setForm((f) => ({ ...f, category: selected.key }));
        setNewCategoryTyped("");
        setConfirmDlg(null);
      },
      onCreate: async () => {
        try {
          const c = await api.post("/material-categories", { label: typed });
          setCategories((cs) => [...cs, c.data]);
          setForm((f) => ({ ...f, category: c.data.key }));
          setNewCategoryTyped("");
          setConfirmDlg(null);
          toast.success(`Added category "${typed}"`);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Failed to add category");
        }
      },
      onCancel: () => {
        setConfirmDlg(null);
        setNewCategoryTyped("");
        // Leave form.category unchanged — Cancel means "no category set".
      },
    });
  };

  /* --- save --- */
  const coverageSameAsUnit = !!form.unit && !!form.coverage_unit && form.coverage_unit === form.unit;
  const selectedUnit = units.find((u) => u.key === form.unit);
  const unitIsDivisible = !!selectedUnit?.is_divisible;

  const save = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name) { setError("Name is required"); return; }
    if (!form.unit) { setError("Unit is required"); return; }
    if (coverageSameAsUnit && !coverageOverride) {
      setError("Coverage unit is the same as the purchase unit — tick \"Save anyway\" under the warning if that really is what you mean.");
      return;
    }
    // Strip UI-only __key fields from the payload.
    const clean_links = (form.supplier_links || [])
      .filter((l) => l.supplier_id)   // ignore blank rows
      .map(({ __key, ...rest }) => rest); // eslint-disable-line no-unused-vars
    const payload = {
      ...form,
      // LT-19b §2 — blank / 0 stores as null; only sent when the unit is divisible.
      stock_length: unitIsDivisible && Number(form.stock_length) > 0 ? Number(form.stock_length) : null,
      // Stage 0 response D1 — semicolon-separated, trimmed, blanks dropped, de-duplicated, order kept.
      keywords: Array.from(new Set((form.keywords || "").split(";").map((k) => k.trim()).filter(Boolean))),
      supplier_links: clean_links,
    };

    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/materials/${id}`, payload);
        toast.success("Material saved");
        navigate("/materials");
      } else {
        const r = await api.post("/materials", payload);
        toast.success(`Material created — ${r.data.code}`);
        navigate("/materials");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const archive = async () => {
    if (!editing || !window.confirm("Archive this material?")) return;
    try {
      await api.delete(`/materials/${id}`);
      toast.success("Material archived");
      navigate("/materials");
    } catch { toast.error("Archive failed"); }
  };

  if (loading) return <div className="app-container py-8 text-ink-3" style={{ fontSize: 12 }}>Loading…</div>;

  const activeCategories = categories.filter((c) => c.status !== "archived");
  const activeUnits = units.filter((u) => u.status !== "archived");
  // LT-18c §2 — never render a unit KEY where the label belongs.
  const unitLabel = (key) => (units.find((u) => u.key === key)?.label || key);

  return (
    <div className="app-container py-8">
      <PageHeader
        title={editing ? (doc?.name || "Material") : "New material"}
        subtitle={editing ? doc?.code : "Add a new material to the master list."}
        breadcrumb={
          <>
            <button onClick={() => navigate("/materials")} className="hover:text-ink-2">Materials</button>
            <ChevronRight size={12} />
            <span className="text-ink-2">{editing ? doc?.code : "New"}</span>
          </>
        }
      />

      <form onSubmit={save}>
        <Card className="p-7">
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            <Field label="Code" hint={editing ? "Code cannot be changed" : "Auto-generated from category if blank (MAT-TIM-…)"}>
              <Input value={form.code} onChange={set("code")} disabled={editing} mono
                placeholder="e.g. MAT-TIM-001" data-testid="material-code-input" />
            </Field>
            <Field label="Name" required>
              <Input value={form.name} onChange={set("name")}
                placeholder="e.g. C24 Treated Timber 47×220mm"
                data-testid="material-name-input" />
            </Field>
            <Field label="Category" hint="Pick from the managed list, or type a new one to add it.">
              <div className="flex gap-2">
                <Select
                  value={form.category}
                  onChange={set("category")}
                  placeholder="Uncategorised"
                  options={activeCategories.map((c) => ({ value: c.key, label: c.label }))}
                  data-testid="material-category-select"
                />
                <Input
                  value={newCategoryTyped}
                  onChange={setNewCategoryTyped}
                  placeholder="or type a new one…"
                  data-testid="material-category-new-input"
                />
                <QuietButton
                  onClick={(e) => {
                    e.preventDefault();
                    if (newCategoryTyped.trim()) openCategoryDialog(newCategoryTyped.trim());
                  }}
                  icon={Plus}
                  data-testid="material-category-add-btn"
                >
                  Add
                </QuietButton>
              </div>
            </Field>
            <Field label="Unit" required
              hint="The unit you buy this in — sheet, pack, bag, m, each. Not what you measure it in on a drawing.">
              <Select value={form.unit} onChange={set("unit")} placeholder="Pick a unit"
                options={activeUnits.map((u) => ({ value: u.key, label: u.label }))}
                data-testid="material-unit-select" />
            </Field>
            <Field label="Current price" required
              hint="Ex-VAT. This is the number the app prices with.">
              <NumberInput value={form.current_price} onChange={set("current_price")}
                prefix="£" suffix={`/ ${form.unit ? unitLabel(form.unit) : "unit"}`}
                data-testid="material-current-price-input" />
            </Field>
            <div />

            {/* LT-18 §6 — new fields */}
            <Field label="Coverage per unit" hint="Optional. e.g. 10 m² per pack of plasterboard.">
              <NumberInput value={form.coverage_per_unit} onChange={set("coverage_per_unit")}
                data-testid="material-coverage-per-unit-input" />
            </Field>
            <Field label="Coverage unit" hint="What the coverage figure is measured in.">
              <Select value={form.coverage_unit} onChange={(v) => { set("coverage_unit")(v); setCoverageOverride(false); }}
                placeholder="—"
                options={activeUnits.map((u) => ({ value: u.key, label: u.label }))}
                data-testid="material-coverage-unit-select" />
            </Field>
            {coverageSameAsUnit && (
              <div className="col-span-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 flex gap-2"
                style={{ fontSize: 12 }} data-testid="material-coverage-warning">
                <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5" />
                <div className="text-amber-900">
                  {/* Approved copy (13/09): line 1 static, line 2 from the record (labels, never keys), line 3 static. */}
                  <div>Coverage converts a purchase unit into a measurement unit.</div>
                  <div data-testid="material-coverage-warning-line2">
                    A material bought in {unitLabel(form.unit)} doesn't also cover
                    {" "}{form.coverage_per_unit != null && form.coverage_per_unit !== "" ? `${form.coverage_per_unit} ` : ""}{unitLabel(form.coverage_unit)}.
                  </div>
                  <div>What do you actually buy this in — a sheet, a pack, a bag?</div>
                  <label className="inline-flex items-center gap-1.5 mt-1.5 text-amber-900" data-testid="material-coverage-override-label">
                    <input type="checkbox" checked={coverageOverride} onChange={(e) => setCoverageOverride(e.target.checked)}
                      data-testid="material-coverage-override" />
                    Save anyway — this is what I mean
                  </label>
                </div>
              </div>
            )}
            <Field label="Cost per coverage" hint="Derived. Current price ÷ coverage per unit.">
              <div className="h-9 px-2.5 rounded-md border border-line-soft bg-surface-2/40 flex items-center"
                data-testid="material-cost-per-coverage">
                {costPerCoverage != null && form.coverage_unit ? (
                  <span className="font-mono tnum text-ink" style={{ fontSize: 13 }}>
                    {gbp(costPerCoverage)} / {unitLabel(form.coverage_unit)}
                  </span>
                ) : (
                  <span className="text-ink-4 italic" style={{ fontSize: 11.5 }}>
                    Set a coverage per unit + unit to see this
                  </span>
                )}
              </div>
            </Field>
            <Field label="Default wastage %"
              hint="Applied when a recipe consumes this material without an explicit override.">
              <NumberInput value={form.default_wastage_pct} onChange={set("default_wastage_pct")}
                suffix="%" data-testid="material-default-wastage-input" />
            </Field>
            {unitIsDivisible && (
              <Field label="Stock length"
                hint="The length this is sold in — 4.8 for 4.8 m timber. Leave blank if it's sold cut to length.">
                <NumberInput value={form.stock_length} onChange={set("stock_length")}
                  suffix={`/ ${unitLabel(form.unit)}`} data-testid="material-stock-length-input" />
              </Field>
            )}
            <Field label="Keywords" hint="Semicolon-separated search words. Stored now, searched later.">
              <Input value={form.keywords} onChange={set("keywords")}
                placeholder="e.g. plasterboard; gypsum; wallboard"
                data-testid="material-keywords-input" />
            </Field>

            <div className="col-span-2">
              <Field label="Notes">
                <Textarea value={form.notes} onChange={set("notes")} rows={3}
                  placeholder="Free-form notes about this material…"
                  data-testid="material-notes-input" />
              </Field>
            </div>
          </div>
        </Card>

        {/* LT-18 §3 — supplier links */}
        <Card className="mt-6">
          <div className="px-5 py-4 border-b border-line flex items-center justify-between">
            <div>
              <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
                Suppliers
              </h3>
              <p className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
                Multiple sources allowed. The preferred one supplies the current price on import.
              </p>
            </div>
            <QuietButton icon={Plus} onClick={(e) => { e.preventDefault(); addLink(); }}
              data-testid="material-add-supplier-link">
              Add another supplier
            </QuietButton>
          </div>
          <div className="px-5 py-5 space-y-4">
            {(form.supplier_links || []).length === 0 ? (
              <div className="text-ink-4 italic text-center py-4" style={{ fontSize: 12 }}
                data-testid="material-no-suppliers">
                No supplier linked. Click "Add another supplier" to attach one.
              </div>
            ) : (
              form.supplier_links.map((link, i) => (
                <SupplierLinkRow
                  key={link.__key || i}
                  link={link}
                  suppliers={suppliers}
                  onChange={(patch) => upsertLink(i, patch)}
                  onRemove={() => removeLink(i)}
                  canRemove={true}
                  onNewSupplierNeeded={openSupplierDialog}
                />
              ))
            )}
          </div>
        </Card>

        {error && (
          <div className="mt-5 px-3 py-2 rounded-md"
            style={{ background: "#FEF3E2", color: "#7A4C0A", fontSize: 12 }}
            data-testid="material-error">
            {error}
          </div>
        )}

        <div className="mt-7 flex items-center justify-between">
          {editing ? (
            <button type="button" onClick={archive}
              className="text-note-red hover:underline inline-flex items-center gap-1.5"
              style={{ fontSize: 12 }}
              data-testid="material-archive-btn">
              <Trash2 size={12} /> Archive material
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <QuietButton onClick={() => navigate("/materials")} icon={ChevronLeft}>Cancel</QuietButton>
            <PrimaryButton type="submit" disabled={saving} icon={saving ? Loader2 : Save}
              data-testid="material-save-btn">
              {editing ? "Save changes" : "Create material"}
            </PrimaryButton>
          </div>
        </div>
      </form>

      {/* LT-18 §5 — canonical LT-28 price ledger. Supplier column is populated
          via the suppliers state loaded above. */}
      {editing && doc?.id && (
        <div className="mt-6">
          <PriceHistoryPanel
            entityType="material"
            entityId={doc.id}
            fieldLabel="price"
            suppliersById={Object.fromEntries(suppliers.map((s) => [s.id, s]))}
          />
        </div>
      )}

      {/* LT-18 §5 — pre-LT-28 legacy inline log. Kept read-only for continuity
          with pre-migration data; the canonical history is the ledger above. */}
      {editing && doc?.price_history?.length > 0 && (
        <Card className="mt-6" data-testid="material-legacy-history">
          <div className="px-5 py-4 border-b border-line">
            <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
              Legacy price history (pre-LT-18)
            </h3>
            <p className="text-ink-3 mt-0.5" style={{ fontSize: 11 }}>
              Read-only historic log. Frozen in LT-18 §5 — no new entries are written here.
              See the ledger above for the canonical history.
            </p>
          </div>
          <table className="w-full text-left">
            <thead>
              <tr className="bg-surface-2/40 border-b border-line">
                <th className="col-head px-4 py-2.5" style={{ width: 130 }}>Date</th>
                <th className="col-head px-4 py-2.5 text-right" style={{ width: 120 }}>Price</th>
                <th className="col-head px-4 py-2.5">Supplier</th>
                <th className="col-head px-4 py-2.5" style={{ width: 110 }}>Source</th>
              </tr>
            </thead>
            <tbody>
              {[...(doc.price_history || [])].reverse().map((p, i) => (
                <tr key={i} className="border-b border-line-soft">
                  <td className="px-4 py-2.5"><span className="font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{ukDate(p.effective_date)}</span></td>
                  <td className="px-4 py-2.5 text-right"><span className="font-mono tnum text-ink" style={{ fontSize: 12.5 }}>{gbp(p.price)}</span></td>
                  <td className="px-4 py-2.5"><span className="text-ink-2" style={{ fontSize: 12 }}>{p.supplier_name || "—"}</span></td>
                  <td className="px-4 py-2.5"><span className="text-ink-3" style={{ fontSize: 11.5 }}>{p.source}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <ConfirmOnNewDialog
        open={!!confirmDlg}
        entity={confirmDlg?.entity}
        typed={confirmDlg?.typed}
        candidates={confirmDlg?.candidates || []}
        onSelectExisting={confirmDlg?.onSelect || (() => {})}
        onCreateNew={confirmDlg?.onCreate || (() => {})}
        onCancel={confirmDlg?.onCancel || (() => {})}
      />
    </div>
  );
}
